import { Component, debug, error, _decorator } from 'cc';
import BindDOMNode from '../../../../common/scripts/Toolkit/Components/DOM/BindDOMNode';
import { joinURL } from '../../../../common/scripts/Toolkit/Utils/Utility';

type PromisfyAllMethod<T extends object, TExcept extends keyof T = never> = {
    [K in Exclude<keyof T, TExcept>]: T[K] extends (...args: infer A) => infer R ? (...args: A) => Promise<Awaited<R>> : T[K];
} & Pick<T, TExcept>;

enum EventType {
    READY = 'READY',
    ERROR = 'ERROR',
    STATE_CHANGE = 'STATE_CHANGE',
    PLAYBACK_QUALITY_CHANGE = 'PLAYBACK_QUALITY_CHANGE',
    PLAYBACK_RATE_CHANGE = 'PLAYBACK_RATE_CHANGE',
    API_CHANGE = 'API_CHANGE',
}

const { ccclass, property, requireComponent } = _decorator;
const EMBED_YOUTUBE_URL = 'https://www.youtube.com/embed/';

@ccclass('YouTubePlayerConfig')
class YouTubePlayerConfig {
    @property
    public readonly videoID: string = '';

    @property
    public readonly loop: boolean = false;

    @property
    public readonly muted: boolean = false;

    @property
    public readonly playsInline: boolean = true;

    @property
    public readonly showFullScreenButton: boolean = true;

    @property
    public readonly showControls: boolean = true;

    @property
    public readonly allowKeyboardControl: boolean = true;

    @property
    public readonly autoPlay: boolean = true;
}

@ccclass('YouTubePlayerController')
@requireComponent(BindDOMNode)
export default class YouTubePlayerController extends Component implements PromisfyAllMethod<YT.Player, 'addEventListener' | 'removeEventListener' | 'destroy'> {
    public static readonly EVENT_TYPE = EventType;

    @property(YouTubePlayerConfig)
    private readonly config: YouTubePlayerConfig = new YouTubePlayerConfig();

    private _bindDomNode: BindDOMNode = null;
    private _player: YT.Player = null;
    private _loop = false;
    private _playerReadyResolve: () => void = null;
    private _playerReadyPromise: Promise<void> = new Promise<void>((resolve) => {
        this._playerReadyResolve = resolve;
    });

    public get bindDomNode() {
        if (!this._bindDomNode) {
            this._bindDomNode = this.getComponent(BindDOMNode);
        }
        return this._bindDomNode;
    }

    public get loop() {
        return this._loop;
    }

    public get player() {
        if (!this._player) {
            error('YoutubePlayer is not initialized');
            return null;
        }
        return this._player;
    }

    public onLoad() {
        this._loop = this.config.loop;
        if (this.config.videoID) {
            this._init(this.config.videoID);
        }
    }

    public onDestroy() {
        if (this._player) {
            if (!this.isReady()) {
                this.waitUntilReady().then(() => this.player.destroy());
            } else {
                this.player.destroy();
            }
        }
    }

    public isReady() {
        return this._player && !!this._player.playVideo;
    }

    public async waitUntilReady() {
        return this._playerReadyPromise;
    }

    /* #region implement all methods of YT.Player with promisfy return type */
    /**
     * Queues a video by ID.
     *
     * @param videoId   Video ID.
     * @param startSeconds   Time from which the video should start playing.
     * @param suggestedQuality   Suggested video player quality.
     */
    public async cueVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    /**
     * Queues a video by ID.
     *
     * @param args   Settings to queue the video.
     */
    public async cueVideoById(args: YT.VideoByIdSettings): Promise<void>;
    public async cueVideoById(args: string | YT.VideoByIdSettings, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void> {
        if (!this._player) {
            this._init(typeof args === 'string' ? args : args.videoId);
        }
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string') {
            this.player.cueVideoById(args, startSeconds, suggestedQuality);
        } else {
            this.player.cueVideoById(args);
        }
    }

    public async loadVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    public async loadVideoById(args: YT.VideoByIdSettings): Promise<void>;
    public async loadVideoById(args: string | YT.VideoByIdSettings, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void> {
        if (!this._player) {
            this._init(typeof args === 'string' ? args : args.videoId);
        }
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string') {
            this.player.loadVideoById(args, startSeconds, suggestedQuality);
        } else {
            this.player.loadVideoById(args);
        }
    }

    public async cueVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    public async cueVideoByUrl(args: YT.VideoByMediaContentUrlSettings): Promise<void>;
    public async cueVideoByUrl(
        args: string | YT.VideoByMediaContentUrlSettings,
        startSeconds?: number,
        suggestedQuality?: YT.SuggestedVideoQuality,
    ): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string') {
            this.player.cueVideoByUrl(args, startSeconds, suggestedQuality);
        } else {
            this.player.cueVideoByUrl(args);
        }
    }

    public async loadVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    public async loadVideoByUrl(args: YT.VideoByMediaContentUrlSettings): Promise<void>;
    public async loadVideoByUrl(
        args: string | YT.VideoByMediaContentUrlSettings,
        startSeconds?: number,
        suggestedQuality?: YT.SuggestedVideoQuality,
    ): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string') {
            this.player.loadVideoByUrl(args, startSeconds, suggestedQuality);
        } else {
            this.player.loadVideoByUrl(args);
        }
    }

    public async cuePlaylist(playlist: string | string[], index?: number, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    public async cuePlaylist(args: YT.IPlaylistSettings): Promise<void>;
    public async cuePlaylist(
        args: string | string[] | YT.IPlaylistSettings,
        index?: number,
        startSeconds?: number,
        suggestedQuality?: YT.SuggestedVideoQuality,
    ): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string' || Array.isArray(args)) {
            this.player.cuePlaylist(args, index, startSeconds, suggestedQuality);
        } else {
            this.player.cuePlaylist(args);
        }
    }

    public async loadPlaylist(playlist: string | string[], index?: number, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality): Promise<void>;
    public async loadPlaylist(args: YT.IPlaylistSettings): Promise<void>;
    public async loadPlaylist(
        args: string | string[] | YT.IPlaylistSettings,
        index?: number,
        startSeconds?: number,
        suggestedQuality?: YT.SuggestedVideoQuality,
    ): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        if (typeof args === 'string' || Array.isArray(args)) {
            this.player.loadPlaylist(args, index, startSeconds, suggestedQuality);
        } else {
            this.player.loadPlaylist(args);
        }
    }

    public async playVideo(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.playVideo();
    }

    public async pauseVideo(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.pauseVideo();
    }

    public async stopVideo(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.stopVideo();
    }

    public async seekTo(seconds: number, allowSeekAhead: boolean): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.seekTo(seconds, allowSeekAhead);
    }

    public async nextVideo(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.nextVideo();
    }

    public async previousVideo(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.previousVideo();
    }

    public async playVideoAt(index: number): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.playVideoAt(index);
    }

    public async mute(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.mute();
    }

    public async unMute(): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.unMute();
    }

    public async isMuted(): Promise<boolean> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.isMuted();
    }

    public async setVolume(volume: number): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setVolume(volume);
    }

    public async getVolume(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getVolume();
    }

    public async setSize(width: number, height: number): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setSize(width, height);
    }

    public async getPlaybackRate(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getPlaybackRate();
    }

    public async setPlaybackRate(suggestedRate: number): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setPlaybackRate(suggestedRate);
    }

    public async getAvailablePlaybackRates(): Promise<number[]> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getAvailablePlaybackRates();
    }

    public async setLoop(loopPlaylists: boolean): Promise<void> {
        this._loop = loopPlaylists;
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setLoop(loopPlaylists);
    }

    public async setShuffle(shufflePlaylist: boolean): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setShuffle(shufflePlaylist);
    }

    public async getVideoLoadedFraction(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getVideoLoadedFraction();
    }

    public async getPlayerState(): Promise<YT.PlayerState> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getPlayerState();
    }

    public async getCurrentTime(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getCurrentTime();
    }

    public async getPlaybackQuality(): Promise<YT.SuggestedVideoQuality> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getPlaybackQuality();
    }

    public async setPlaybackQuality(suggestedQuality: YT.SuggestedVideoQuality): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setPlaybackQuality(suggestedQuality);
    }

    public async getAvailableQualityLevels(): Promise<YT.SuggestedVideoQuality[]> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getAvailableQualityLevels();
    }

    public async getDuration(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getDuration();
    }

    public async getVideoUrl(): Promise<string> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getVideoUrl();
    }

    public async getSphericalProperties(): Promise<YT.SphericalProperties> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getSphericalProperties();
    }

    public async setSphericalProperties(option: YT.SphericalProperties): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.setSphericalProperties(option);
    }

    public async getVideoEmbedCode(): Promise<string> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getVideoEmbedCode();
    }

    public async getPlaylist(): Promise<string[]> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getPlaylist();
    }

    public async getPlaylistIndex(): Promise<number> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getPlaylistIndex();
    }

    public async addEventListener<TEventName extends keyof YT.Events>(eventName: TEventName, listener: NonNullable<YT.Events[TEventName]>): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.addEventListener(eventName, listener);
    }

    public async removeEventListener<TEventName extends keyof YT.Events>(eventName: TEventName, listener: NonNullable<YT.Events[TEventName]>): Promise<void> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        this.player.removeEventListener(eventName, listener);
    }

    public async getIframe(): Promise<HTMLIFrameElement> {
        if (!this.isReady()) {
            await this.waitUntilReady();
        }
        return this.player.getIframe();
    }
    /* #endregion */

    private _init(videoID: string) {
        if (this._player) {
            error('YoutubePlayer is already initialized');
            return;
        }

        // set iframe src
        const iframe = this.bindDomNode.element as HTMLIFrameElement;
        if (!iframe.contentWindow) {
            error('YoutubePlayer need to bind with an iframe element');
            return;
        }
        iframe.src = this._buildURL(videoID);

        // create player
        const player = new YT.Player(this.bindDomNode.element.id);
        // add event listener
        player.addEventListener('onReady', (arg) => {
            this._playerReadyResolve();
            debug('onReady', arg);
            this.emit(EventType.READY, arg);
        });
        player.addEventListener('onStateChange', (arg) => {
            if (arg.data === YT.PlayerState.ENDED && this.loop) {
                player.playVideo();
            }
            debug('onStateChange: ', arg);
            this.emit(EventType.STATE_CHANGE, arg);
        });
        player.addEventListener('onPlaybackQualityChange', (arg) => {
            debug('onPlaybackQualityChange: ', arg);
            this.emit(EventType.PLAYBACK_QUALITY_CHANGE, arg);
        });
        player.addEventListener('onPlaybackRateChange', (arg) => {
            debug('onPlaybackRateChange: ', arg);
            this.emit(EventType.PLAYBACK_RATE_CHANGE, arg);
        });
        player.addEventListener('onError', (arg) => {
            debug('onError: ', arg);
            this.emit(EventType.ERROR, arg);
        });
        player.addEventListener('onApiChange', (arg) => {
            debug('onApiChange: ', arg);
            this.emit(EventType.API_CHANGE, arg);
        });
        this._player = player;
    }

    private _buildURL(videoID: string) {
        const url = new URL(joinURL(EMBED_YOUTUBE_URL, videoID));
        const params = new URLSearchParams();
        params.append('enablejsapi', '1');
        params.append('autoplay', this.config.autoPlay ? '1' : '0');
        // FIX it is official suggestion but not working somehow, hence we control loop manually while state changed
        // params.append('loop', this.config.loop ? '1' : '0');
        // params.append('playlist', videoID);
        params.append('controls', this.config.showControls ? '1' : '0');
        params.append('mute', this.config.muted ? '1' : '0');
        params.append('playsinline', this.config.playsInline ? '1' : '0');
        params.append('fs', this.config.showFullScreenButton ? '1' : '0');
        params.append('disablekb', this.config.allowKeyboardControl ? '0' : '1');
        params.append('origin', document.location.origin);
        url.search = params.toString();
        return url.href;
    }
}
